// src/pages/AttendanceRegister.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  UserGroupIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';

const TODAY = new Date().toISOString().split('T')[0];
const EMPTY_SUMMARY = {
  present: 0,
  absent: 0,
  totalStudents: 0,
  markedSessions: 0,
};

const asArray = (response) => {
  const value = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(value)) return value;
  return value?.rows || value?.terms || value?.items || [];
};

const getTermName = (term, index = 0) =>
  term?.name || `Term ${term?.term_number || index + 1}`;

const AttendanceRegister = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [terms, setTerms] = useState([]);
  const [date, setDate] = useState(TODAY);
  const [view, setView] = useState('daily');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('all');
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState('');
  const [dailyRows, setDailyRows] = useState([]);
  const [dailySummary, setDailySummary] = useState(EMPTY_SUMMARY);
  const [termReport, setTermReport] = useState(null);
  const [yearReport, setYearReport] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchSetup = useCallback(async () => {
    try {
      setInitialLoading(true);
      const [classResponse, studentsResponse, termsResponse] = await Promise.all([
        apiClient.get(`/classes/${id}`),
        apiClient.get(`/classes/${id}/students`, { params: { limit: 500 } }),
        apiClient.get('/attendance/terms'),
      ]);

      setClassData(classResponse.data?.data || classResponse.data);
      const studentItems = asArray(studentsResponse);
      const termItems = asArray(termsResponse).sort((a, b) =>
        String(a.start_date || '').localeCompare(String(b.start_date || ''))
      );

      setStudents(studentItems);
      setTerms(termItems);

      const activeTerm = termItems.find((term) => {
        const start = term.start_date;
        const end = term.end_date;
        return start && end && TODAY >= start && TODAY <= end;
      }) || termItems[termItems.length - 1];

      if (activeTerm) {
        setSelectedTermId(String(activeTerm.id));
        setSelectedAcademicYearId(String(activeTerm.academic_year_id || ''));
      }
    } catch (error) {
      console.error('Failed to load attendance register:', error);
      toast.error('Failed to load attendance register');
    } finally {
      setInitialLoading(false);
    }
  }, [id]);

  const fetchDailyAttendance = useCallback(async () => {
    if (!id || students.length === 0) return;

    try {
      setReportLoading(true);
      const response = await apiClient.get(`/attendance/date/${date}`, {
        params: { classId: id },
      });
      const records = asArray(response);
      const recordMap = new Map(
        records.map((record) => [
          `${Number(record.student_id)}:${record.session}`,
          record,
        ])
      );

      const counts = { ...EMPTY_SUMMARY, totalStudents: students.length };
      records.forEach((record) => {
        if (Object.prototype.hasOwnProperty.call(counts, record.status)) {
          counts[record.status] += 1;
        }
        counts.markedSessions += 1;
      });

      setDailySummary(counts);
      setDailyRows(students.map((student) => ({
        studentId: student.id,
        name: `${student.first_name || ''} ${student.last_name || ''}`.trim(),
        admissionNumber: student.admission_number,
        gender: student.gender,
        morning: recordMap.get(`${Number(student.id)}:morning`) || null,
        afternoon: recordMap.get(`${Number(student.id)}:afternoon`) || null,
      })));
    } catch (error) {
      console.error('Failed to load daily attendance:', error);
      toast.error('Failed to load daily attendance');
    } finally {
      setReportLoading(false);
    }
  }, [date, id, students]);

  const fetchTermReport = useCallback(async () => {
    if (!id || !selectedTermId) return;

    try {
      setReportLoading(true);
      const response = await apiClient.get(`/attendance/report/class/${id}`, {
        params: { termId: selectedTermId },
      });
      setTermReport(response.data?.data || null);
    } catch (error) {
      console.error('Failed to load term attendance report:', error);
      toast.error(error.response?.data?.message || 'Failed to load term report');
      setTermReport(null);
    } finally {
      setReportLoading(false);
    }
  }, [id, selectedTermId]);

  const fetchYearReport = useCallback(async () => {
    if (!id || !selectedAcademicYearId) return;

    try {
      setReportLoading(true);
      const response = await apiClient.get(
        `/attendance/report/class/${id}/academic-year/${selectedAcademicYearId}`
      );
      setYearReport(response.data?.data || null);
    } catch (error) {
      console.error('Failed to load academic-year report:', error);
      toast.error(error.response?.data?.message || 'Failed to load academic-year report');
      setYearReport(null);
    } finally {
      setReportLoading(false);
    }
  }, [id, selectedAcademicYearId]);

  useEffect(() => {
    fetchSetup();
  }, [fetchSetup]);

  useEffect(() => {
    if (!initialLoading && view === 'daily') fetchDailyAttendance();
  }, [fetchDailyAttendance, initialLoading, view]);

  useEffect(() => {
    if (!initialLoading && ['weekly', 'term'].includes(view)) fetchTermReport();
  }, [fetchTermReport, initialLoading, view]);

  useEffect(() => {
    if (!initialLoading && view === 'year') fetchYearReport();
  }, [fetchYearReport, initialLoading, view]);

  useEffect(() => {
    setSelectedWeek('all');
    const term = terms.find((item) => String(item.id) === String(selectedTermId));
    if (term?.academic_year_id) {
      setSelectedAcademicYearId(String(term.academic_year_id));
    }
  }, [selectedTermId, terms]);

  const weekOptions = useMemo(() => {
    const values = new Set();
    (termReport?.students || []).forEach((student) => {
      (student.weeks || []).forEach((week) => values.add(week.week));
    });
    return [...values].sort((a, b) => a - b);
  }, [termReport]);

  const weeklyRows = useMemo(() => {
    if (!termReport) return [];
    return termReport.students.map((student) => {
      const weeks = selectedWeek === 'all'
        ? student.weeks || []
        : (student.weeks || []).filter(
            (week) => String(week.week) === String(selectedWeek)
          );
      const possibleSessions = weeks.reduce((sum, week) => sum + week.sessionsOpened, 0);
      const attendedSessions = weeks.reduce((sum, week) => sum + week.attendedSessions, 0);
      return {
        ...student,
        weekly: {
          possibleSessions,
          attendedSessions,
          present: weeks.reduce((sum, week) => sum + week.present, 0),
          absent: weeks.reduce((sum, week) => sum + week.absent, 0),
          attendancePercentage: possibleSessions
            ? Number(((attendedSessions / possibleSessions) * 100).toFixed(2))
            : 0,
        },
      };
    });
  }, [selectedWeek, termReport]);

  const academicYears = useMemo(() => {
    const map = new Map();
    terms.forEach((term) => {
      const yearId = term.academic_year_id;
      if (yearId) {
        map.set(String(yearId), term.academicYear?.name || term.academic_year_name || `Academic Year ${yearId}`);
      }
    });
    return [...map.entries()];
  }, [terms]);

  const statusBadge = (record) => {
    const status = record?.status || 'unmarked';
    const styles = {
      present: 'bg-green-50 text-green-700 border-green-200',
      absent: 'bg-red-50 text-red-700 border-red-200',
      unmarked: 'bg-gray-50 text-gray-400 border-gray-200',
    };
    return (
      <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium capitalize ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const percentageBadge = (value) => {
    const number = Number(value || 0);
    const color = number >= 90
      ? 'bg-green-100 text-green-700'
      : number >= 75
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700';
    return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${color}`}>{number.toFixed(2)}%</span>;
  };

  if (initialLoading) {
    return <FireLoader fullScreen size="lg" text="Loading..." />;
  }

  const tabs = [
    ['daily', 'Daily'],
    ['weekly', 'Weekly'],
    ['term', 'Term'],
    ['year', 'Academic Year'],
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-5 sm:px-6">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => navigate(`/app/classes/${id}`)}
          className="rounded-xl p-2 text-gray-500 hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">
            {classData?.name || `Class #${id}`} Attendance Register
          </h1>
          <p className="mt-1 text-sm text-gray-500">Morning and afternoon attendance, weekly totals and term percentages</p>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/app/classes/${id}/attendance/mark`)}
          className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          Mark Attendance
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white p-1">
        <div className="flex min-w-max gap-1">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                view === key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {view === 'daily' ? (
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
          ) : view === 'year' ? (
            <select
              value={selectedAcademicYearId}
              onChange={(event) => setSelectedAcademicYearId(event.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            >
              <option value="">Select academic year</option>
              {academicYears.map(([yearId, name]) => <option key={yearId} value={yearId}>{name}</option>)}
            </select>
          ) : (
            <>
              <select
                value={selectedTermId}
                onChange={(event) => setSelectedTermId(event.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              >
                <option value="">Select term</option>
                {terms.map((term, index) => <option key={term.id} value={term.id}>{getTermName(term, index)}</option>)}
              </select>
              {view === 'weekly' && (
                <select
                  value={selectedWeek}
                  onChange={(event) => setSelectedWeek(event.target.value)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
                >
                  <option value="all">All weeks</option>
                  {weekOptions.map((week) => <option key={week} value={week}>Week {week}</option>)}
                </select>
              )}
            </>
          )}
          <button
            type="button"
            onClick={view === 'daily' ? fetchDailyAttendance : view === 'year' ? fetchYearReport : fetchTermReport}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Refresh
          </button>
        </div>
      </div>

      {reportLoading ? (
        <div className="flex justify-center rounded-xl bg-white py-16"><FireLoader size="lg" text="Loading..." /></div>
      ) : view === 'daily' ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {[
              ['Students', dailySummary.totalStudents, UserGroupIcon, 'blue'],
              ['Present', dailySummary.present, CheckCircleIcon, 'green'],
              ['Absent', dailySummary.absent, XCircleIcon, 'red'],
            ].map(([label, value, Icon]) => (
              <div key={label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <Icon className="mb-2 h-5 w-5 text-primary-600" />
                <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
              </div>
            ))}
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50"><tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Student</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Morning</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Afternoon</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {dailyRows.map((row) => <tr key={row.studentId}>
                    <td className="px-4 py-3"><p className="font-medium text-gray-800">{row.name}</p><p className="text-xs text-gray-400">{row.admissionNumber || 'No admission number'}</p></td>
                    <td className="px-4 py-3">{statusBadge(row.morning)}</td>
                    <td className="px-4 py-3">{statusBadge(row.afternoon)}</td>
                  </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : view === 'weekly' ? (
        <ReportTable rows={weeklyRows.map((student) => ({ ...student, totals: student.weekly }))} percentageBadge={percentageBadge} />
      ) : view === 'term' ? (
        <>
          <RegisterSummary report={termReport} percentageBadge={percentageBadge} />
          <ReportTable rows={termReport?.students || []} percentageBadge={percentageBadge} />
        </>
      ) : (
        <YearReport report={yearReport} percentageBadge={percentageBadge} />
      )}
    </div>
  );
};

const RegisterSummary = ({ report, percentageBadge }) => {
  if (!report) return <EmptyReport />;
  const values = [
    ['Students on Roll', report.register?.studentsOnRoll || 0],
    ['Days Opened', report.register?.daysOpened || 0],
    ['Sessions Opened', report.register?.sessionsOpened || 0],
    ['Class Percentage', percentageBadge(report.register?.attendancePercentage)],
  ];
  return <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{values.map(([label, value]) => <div key={label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"><p className="text-xs uppercase text-gray-400">{label}</p><div className="mt-2 text-xl font-bold text-gray-800">{value}</div></div>)}</div>;
};

const ReportTable = ({ rows, percentageBadge }) => (
  <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
    {rows.length === 0 ? <EmptyReport /> : <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-100">
      <thead className="bg-gray-50"><tr>{['Student', 'Possible', 'Present', 'Absent', 'Attendance'].map((heading) => <th key={heading} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{heading}</th>)}</tr></thead>
      <tbody className="divide-y divide-gray-100">{rows.map((student) => <tr key={student.studentId}>
        <td className="px-4 py-3"><p className="font-medium text-gray-800">{student.name}</p><p className="text-xs text-gray-400">{student.admissionNumber || '—'}</p></td>
        <td className="px-4 py-3 text-sm text-gray-600">{student.totals?.possibleSessions || 0}</td>
        <td className="px-4 py-3 text-sm text-green-700">{student.totals?.present || 0}</td>
        <td className="px-4 py-3 text-sm text-red-700">{student.totals?.absent || 0}</td>
        <td className="px-4 py-3">{percentageBadge(student.totals?.attendancePercentage)}</td>
      </tr>)}</tbody>
    </table></div>}
  </div>
);

const YearReport = ({ report, percentageBadge }) => {
  if (!report) return <EmptyReport />;
  return <div className="space-y-5">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{(report.terms || []).map((term) => <div key={term.period?.termId} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"><p className="font-semibold text-gray-800">{term.period?.name}</p><p className="mt-1 text-sm text-gray-500">{term.period?.fromDate} to {term.period?.toDate}</p><div className="mt-3">{percentageBadge(term.register?.attendancePercentage)}</div></div>)}</div>
    <div><h2 className="mb-3 text-lg font-bold text-gray-800">Combined Three-Term Attendance</h2><ReportTable rows={(report.cumulativeStudents || []).map((student) => ({ ...student, totals: student }))} percentageBadge={percentageBadge} /></div>
  </div>;
};

const EmptyReport = () => <div className="rounded-xl border border-gray-100 bg-white py-12 text-center"><CalendarDaysIcon className="mx-auto h-10 w-10 text-gray-300" /><p className="mt-3 text-sm text-gray-500">No attendance data is available for this period.</p></div>;

export default AttendanceRegister;